import * as React from "react";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Boton,
  Campo,
  Dialog,
  DialogContenido,
  DialogDescripcion,
  DialogDisparador,
  DialogEncabezado,
  DialogPie,
  DialogTitulo,
  EsqueletoTabla,
  Input,
  Pill,
  PuntoNaranja,
  Tabla,
  TablaCabecera,
  TablaCelda,
  TablaCuerpo,
  TablaEncabezado,
  TablaFila,
  Textarea,
  TituloPagina,
  useToast,
  Vacio,
} from "@iavmx/ui";
import { leer } from "../api";
import { Candado, ErrorEnLinea, FilaTiempo, Miga, PillEtapa, Seccion } from "../componentes";
import { useProductos } from "../datos";
import { fechaCorta, hoyIso, mxn, relativo } from "../formato";
import { useSesion } from "../sesion";
import { ETIQUETA_ESTADO_COTIZACION, ETIQUETA_ESTADO_PROPUESTA, type Etapa, type RespuestaCuenta } from "../tipos";

const TIPO_ACTIVIDAD: Record<string, string> = { llamada: "Llamada", visita: "Visita", reunion: "Reunión", propuesta: "Propuesta", tarea: "Tarea", correo: "Correo", whatsapp: "WhatsApp" };

/** Campos de próxima acción obligatoria, reutilizados en alta, etapa y actividad. */
export function CamposProximaAccion({ accion, fecha, onAccion, onFecha, ayuda }: { accion: string; fecha: string; onAccion: (v: string) => void; onFecha: (v: string) => void; ayuda?: string }) {
  return (
    <div className="grid gap-4 sm:grid-cols-[minmax(0,1fr)_160px]">
      <Campo etiqueta="Próxima acción · obligatoria" htmlFor="pa" ayuda={ayuda ?? "Qué vas a hacer. Ninguna oportunidad se guarda sin próxima acción y fecha."}>
        <Input id="pa" value={accion} onChange={(e) => onAccion(e.target.value)} placeholder="Llamar a Ana Ruiz para revisar la propuesta" required minLength={3} className="text-base sm:text-sm" />
      </Campo>
      <Campo etiqueta="Fecha" htmlFor="paf">
        <Input id="paf" type="date" value={fecha} min={hoyIso()} onChange={(e) => onFecha(e.target.value)} required className="text-base sm:text-sm" />
      </Campo>
    </div>
  );
}

function NuevaOportunidad({ cuentaId, contactos }: { cuentaId: string; contactos: { id: string; nombre: string }[] }) {
  const { api } = useSesion();
  const qc = useQueryClient();
  const navegar = useNavigate();
  const { avisar } = useToast();
  const productos = useProductos();
  const [abierto, setAbierto] = React.useState(false);
  const [nombre, setNombre] = React.useState("");
  const [valor, setValor] = React.useState("");
  const [cierre, setCierre] = React.useState("");
  const [contactoId, setContactoId] = React.useState<string>(contactos[0]?.id ?? "");
  const [prods, setProds] = React.useState<string[]>([]);
  const [accion, setAccion] = React.useState("");
  const [fecha, setFecha] = React.useState(hoyIso(1));
  const crear = useMutation({
    mutationFn: async () => {
      const r = await api.api.oportunidades.$post({
        json: {
          cuentaId,
          contactoId: contactoId || null,
          nombre: nombre.trim(),
          valor: valor ? Number(valor) : null,
          fechaCierreEstimada: cierre || null,
          productos: prods,
          proximaAccion: accion,
          proximaAccionFecha: fecha,
        },
      });
      return leer<{ id: string }>(r);
    },
    onSuccess: ({ id }) => {
      avisar({ titulo: "Oportunidad creada", tono: "exito" });
      void qc.invalidateQueries({ queryKey: ["cuenta", cuentaId] });
      setAbierto(false);
      void navegar({ to: "/oportunidades/$id", params: { id } });
    },
  });
  const valida = nombre.trim().length >= 2 && accion.trim().length >= 3 && /^\d{4}-\d{2}-\d{2}$/.test(fecha);
  return (
    <Dialog open={abierto} onOpenChange={setAbierto}>
      <DialogDisparador asChild>
        <Boton variante="acento" tamano="sm">
          Nueva oportunidad
        </Boton>
      </DialogDisparador>
      <DialogContenido className="max-h-[90vh] overflow-y-auto">
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (valida) crear.mutate();
          }}
        >
          <DialogEncabezado>
            <DialogTitulo>Nueva oportunidad</DialogTitulo>
            <DialogDescripcion>Entra al pipeline como lead. Se mueve de etapa desde su ficha.</DialogDescripcion>
          </DialogEncabezado>
          <Campo etiqueta="Nombre de la oportunidad" htmlFor="on">
            <Input id="on" value={nombre} onChange={(e) => setNombre(e.target.value)} placeholder="Campaña Buen Fin 2026" required autoFocus className="text-base sm:text-sm" />
          </Campo>
          <div className="grid gap-4 sm:grid-cols-2">
            <Campo etiqueta="Valor estimado (MXN)" htmlFor="ov" ayuda="Sin IVA. Puede ir vacío en un lead.">
              <Input id="ov" type="number" min={0} step={1000} inputMode="numeric" value={valor} onChange={(e) => setValor(e.target.value)} className="text-base sm:text-sm" />
            </Campo>
            <Campo etiqueta="Cierre estimado" htmlFor="oc">
              <Input id="oc" type="date" value={cierre} onChange={(e) => setCierre(e.target.value)} className="text-base sm:text-sm" />
            </Campo>
          </div>
          {contactos.length > 0 && (
            <Campo etiqueta="Contacto" htmlFor="ocont">
              <select id="ocont" value={contactoId} onChange={(e) => setContactoId(e.target.value)} className="flex h-10 w-full border border-input bg-fondo px-3 font-ui text-base text-tinta sm:text-sm">
                <option value="">Sin contacto</option>
                {contactos.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.nombre}
                  </option>
                ))}
              </select>
            </Campo>
          )}
          <fieldset>
            <legend className="mb-1.5 block font-ui text-xs font-semibold uppercase tracking-wide text-tinta-3">Productos de interés</legend>
            <div className="flex flex-wrap gap-1.5">
              {(productos.data?.productos ?? []).map((p) => {
                const on = prods.includes(p.clave);
                return (
                  <label key={p.id} className={`inline-flex h-8 cursor-pointer items-center gap-1.5 rounded-full border px-3 font-ui text-xs font-semibold ${on ? "border-tinta bg-tinta text-tinta-inversa" : "border-linea-fuerte text-tinta-2"}`}>
                    <input type="checkbox" className="sr-only" checked={on} onChange={() => setProds((v) => (on ? v.filter((x) => x !== p.clave) : [...v, p.clave]))} />
                    {p.nombre}
                  </label>
                );
              })}
            </div>
          </fieldset>
          <CamposProximaAccion accion={accion} fecha={fecha} onAccion={setAccion} onFecha={setFecha} />
          <ErrorEnLinea error={crear.error} />
          <DialogPie>
            <Boton type="button" variante="contorno" onClick={() => setAbierto(false)}>
              Cancelar
            </Boton>
            <Boton type="submit" disabled={!valida || crear.isPending}>
              {crear.isPending ? "Guardando…" : "Crear oportunidad"}
            </Boton>
          </DialogPie>
        </form>
      </DialogContenido>
    </Dialog>
  );
}

function NuevoContacto({ cuentaId }: { cuentaId: string }) {
  const { api } = useSesion();
  const qc = useQueryClient();
  const [abierto, setAbierto] = React.useState(false);
  const [f, setF] = React.useState({ nombre: "", puesto: "", telefono: "", correo: "", principal: false });
  const crear = useMutation({
    mutationFn: () => api.api.cuentas[":id"].contactos.$post({ param: { id: cuentaId }, json: { nombre: f.nombre.trim(), puesto: f.puesto || null, telefono: f.telefono || null, correo: f.correo || null, principal: f.principal } }).then((r) => leer<{ id: string }>(r)),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["cuenta", cuentaId] });
      setAbierto(false);
      setF({ nombre: "", puesto: "", telefono: "", correo: "", principal: false });
    },
  });
  return (
    <Dialog open={abierto} onOpenChange={setAbierto}>
      <DialogDisparador asChild>
        <Boton variante="liga" tamano="sm">
          Agregar contacto
        </Boton>
      </DialogDisparador>
      <DialogContenido>
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (f.nombre.trim().length >= 2) crear.mutate();
          }}
        >
          <DialogEncabezado>
            <DialogTitulo>Nuevo contacto</DialogTitulo>
          </DialogEncabezado>
          <Campo etiqueta="Nombre" htmlFor="cn">
            <Input id="cn" value={f.nombre} onChange={(e) => setF({ ...f, nombre: e.target.value })} required autoFocus className="text-base sm:text-sm" />
          </Campo>
          <Campo etiqueta="Puesto" htmlFor="cp">
            <Input id="cp" value={f.puesto} onChange={(e) => setF({ ...f, puesto: e.target.value })} className="text-base sm:text-sm" />
          </Campo>
          <div className="grid gap-4 sm:grid-cols-2">
            <Campo etiqueta="Celular" htmlFor="ct">
              <Input id="ct" type="tel" value={f.telefono} onChange={(e) => setF({ ...f, telefono: e.target.value })} className="text-base sm:text-sm" />
            </Campo>
            <Campo etiqueta="Correo" htmlFor="cc">
              <Input id="cc" type="email" value={f.correo} onChange={(e) => setF({ ...f, correo: e.target.value })} className="text-base sm:text-sm" />
            </Campo>
          </div>
          <label className="flex items-center gap-2 font-ui text-sm">
            <input type="checkbox" checked={f.principal} onChange={(e) => setF({ ...f, principal: e.target.checked })} className="size-4 accent-tinta" />
            Es el contacto principal
          </label>
          <ErrorEnLinea error={crear.error} />
          <DialogPie>
            <Boton type="button" variante="contorno" onClick={() => setAbierto(false)}>
              Cancelar
            </Boton>
            <Boton type="submit" disabled={crear.isPending}>
              Guardar contacto
            </Boton>
          </DialogPie>
        </form>
      </DialogContenido>
    </Dialog>
  );
}

export function Cuenta() {
  const { id } = useParams({ from: "/cuentas/$id" });
  const { api, listo } = useSesion();
  const q = useQuery({ queryKey: ["cuenta", id], queryFn: () => api.api.cuentas[":id"].$get({ param: { id } }).then((r) => leer<RespuestaCuenta>(r)), enabled: listo });
  if (q.isLoading) return <EsqueletoTabla />;
  if (q.error || !q.data) return <Vacio titulo="No se pudo abrir la cuenta" texto={q.error?.message} />;
  const { cuenta, contactos, oportunidades, cotizaciones, propuestas, actividades } = q.data;
  return (
    <>
      <Miga partes={[{ texto: "Cuentas", to: "/cuentas" }, { texto: cuenta.razonSocial }]} />
      <TituloPagina
        descripcion={[cuenta.nombreComercial, cuenta.segmento, cuenta.domicilio, cuenta.rfc ? `RFC ${cuenta.rfc}` : null].filter(Boolean).join(" · ") || "Sin datos fiscales todavía."}
        accion={<NuevaOportunidad cuentaId={cuenta.id} contactos={contactos} />}
      >
        {cuenta.razonSocial}
        <PuntoNaranja />
      </TituloPagina>
      <div className="mb-6 flex flex-wrap items-center gap-2 font-ui text-sm text-tinta-3">
        <Pill tono={cuenta.estado === "activo" ? "verde" : "neutro"}>{cuenta.estado}</Pill>
        <span>Ejecutivo: {cuenta.ejecutivo ?? "sin asignar"}</span>
        {cuenta.origen && <span>· Origen: {cuenta.origen}</span>}
        {cuenta.odooId && <span>· Odoo #{cuenta.odooId}</span>}
        {cuenta.telefono && <span>· {cuenta.telefono}</span>}
        {cuenta.correo && <span>· {cuenta.correo}</span>}
      </div>

      <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_360px]">
        <div className="min-w-0 space-y-8">
          <Seccion titulo="Oportunidades">
            {oportunidades.length === 0 ? (
              <p className="font-ui text-sm text-tinta-tenue">Sin oportunidades. Crea la primera con el botón de arriba.</p>
            ) : (
              <Tabla>
                <TablaCabecera>
                  <TablaFila>
                    <TablaEncabezado>Oportunidad</TablaEncabezado>
                    <TablaEncabezado>Etapa</TablaEncabezado>
                    <TablaEncabezado className="text-right">Valor</TablaEncabezado>
                    <TablaEncabezado>Próxima acción</TablaEncabezado>
                    <TablaEncabezado>Ejecutivo</TablaEncabezado>
                  </TablaFila>
                </TablaCabecera>
                <TablaCuerpo>
                  {oportunidades.map((o) => (
                    <TablaFila key={o.id}>
                      <TablaCelda>
                        <Link to="/oportunidades/$id" params={{ id: o.id }} className="font-semibold hover:underline">
                          {o.nombre}
                        </Link>
                      </TablaCelda>
                      <TablaCelda>
                        <PillEtapa etapa={o.etapa as Etapa} />
                      </TablaCelda>
                      <TablaCelda className="text-right tabular-nums">{o.montoVisible ? (o.valor ? mxn(o.valor) : "—") : <Candado texto="no visible" />}</TablaCelda>
                      <TablaCelda>
                        <div className="font-ui text-sm">{o.proximaAccion}</div>
                        <div className="font-ui text-xs text-tinta-tenue">{fechaCorta(o.proximaAccionFecha)}</div>
                      </TablaCelda>
                      <TablaCelda>{o.ejecutivo ?? "—"}</TablaCelda>
                    </TablaFila>
                  ))}
                </TablaCuerpo>
              </Tabla>
            )}
          </Seccion>

          <Seccion
            titulo="Cotizaciones"
            accion={
              <Boton variante="liga" tamano="sm" asChild>
                <Link to="/cotizaciones/nueva" search={{ cuenta: cuenta.id, oportunidad: oportunidades.find((o) => !["ganado", "perdido"].includes(o.etapa))?.id }}>
                  Nueva cotización
                </Link>
              </Boton>
            }
          >
            {cotizaciones.length === 0 ? (
              <p className="font-ui text-sm text-tinta-tenue">Sin cotizaciones tuyas en esta cuenta. Solo tú y la gerencia ven las tuyas.</p>
            ) : (
              <ul className="divide-y divide-linea">
                {cotizaciones.map((c) => (
                  <li key={c.id} className="flex items-center justify-between gap-3 py-2 font-ui text-sm">
                    <Link to="/cotizaciones/$id" params={{ id: c.id }} className="font-semibold hover:underline">
                      COT-{String(c.folio).padStart(4, "0")}
                    </Link>
                    <span className="text-tinta-tenue">{ETIQUETA_ESTADO_COTIZACION[c.estado]}</span>
                    <span className="tabular-nums">{mxn(c.total)}</span>
                  </li>
                ))}
              </ul>
            )}
          </Seccion>

          <Seccion titulo="Propuestas">
            {propuestas.length === 0 ? (
              <p className="font-ui text-sm text-tinta-tenue">Sin propuestas todavía. Se crean desde una cotización.</p>
            ) : (
              <ul className="divide-y divide-linea">
                {propuestas.map((p) => (
                  <li key={p.id} className="flex items-center justify-between gap-3 py-2 font-ui text-sm">
                    <Link to="/propuestas/$id" params={{ id: p.id }} className="font-semibold hover:underline">
                      {p.titulo}
                    </Link>
                    <Pill tono={p.estado === "aceptada" ? "verde" : p.estado === "cambios_pedidos" ? "ambar" : "azul"}>{ETIQUETA_ESTADO_PROPUESTA[p.estado]}</Pill>
                  </li>
                ))}
              </ul>
            )}
          </Seccion>
        </div>

        <aside className="min-w-0 space-y-8">
          <Seccion titulo="Contactos" accion={<NuevoContacto cuentaId={cuenta.id} />}>
            {contactos.length === 0 ? (
              <p className="font-ui text-sm text-tinta-tenue">Sin contactos.</p>
            ) : (
              <ul className="space-y-3">
                {contactos.map((c) => (
                  <li key={c.id} className="font-ui text-sm">
                    <div className="font-semibold">
                      {c.nombre} {c.principal && <Pill tono="neutro">principal</Pill>}
                    </div>
                    <div className="text-xs text-tinta-tenue">{[c.puesto, c.telefono, c.correo].filter(Boolean).join(" · ")}</div>
                  </li>
                ))}
              </ul>
            )}
          </Seccion>
          <Seccion titulo="Actividad reciente">
            {actividades.length === 0 ? (
              <p className="font-ui text-sm text-tinta-tenue">Sin actividades registradas.</p>
            ) : (
              <ul>
                {actividades.map((a) => (
                  <FilaTiempo key={a.id} titulo={a.resumen ?? TIPO_ACTIVIDAD[a.tipo] ?? a.tipo} detalle={[a.ejecutivo, a.resultado].filter(Boolean).join(" · ")} en={a.fecha} azul={a.automatica} />
                ))}
              </ul>
            )}
            <p className="font-ui text-xs text-tinta-tenue">Última actividad {cuenta.ultimaActividadEn ? relativo(cuenta.ultimaActividadEn) : "—"}. Notas y tipos con más detalle en cada oportunidad.</p>
          </Seccion>
        </aside>
      </div>
      <Textarea className="hidden" aria-hidden readOnly />
    </>
  );
}
